import type React from 'react'
import { useState, useContext } from 'react'
import styled from 'styled-components'

import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import DateInput from '@/components/input/DateInput'
import ctx from '@/context/AppContext'
import { graphQLQuery } from '@/hooks/hooks'
import QUERIES from '@/lib/graphql'
import type { Application } from '@/types/Activity'
import type { ILandlordPropertyReference, RentDeduction } from '@/types/Landlord'
import { Colours } from '@/utils/styles'

interface Props {
  setOpen: (open: boolean) => void
  application: Application
  reference: ILandlordPropertyReference
}

const ItemHeader = styled.h2`
  font-family: Montserrat;
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  -webkit-letter-spacing: 0.21px;
  -moz-letter-spacing: 0.21px;
  -ms-letter-spacing: 0.21px;
  letter-spacing: 0.21px;
  margin-bottom: 0;
  text-align: left;

  #document-proof {
    font-weight: normal;
  }
`

const Container = styled.form`
  display: grid;
  grid-template-rows: 100px 1fr 1fr;
`

const InputGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));

  span:not(#text-input) {
    display: none;
  }
`

const Select = styled.select`
  margin: 10px 5px;
  background: white;
  border-style: solid;
  border-color: rgba(188, 190, 192, 0.5);
  border-width: 1px;
  padding: 18px;
  border-radius: 5px;
  position: relative;
  text-align: left;
  outline: none;
`

const ErrorContainer = styled.div`
  strong {
    color: ${Colours.red};
  }
`

const WEEKS_NOTICE = [
  { value: 1, label: 'One Week' },
  { value: 2, label: 'Two Weeks' },
  { value: 3, label: 'Three Weeks' },
  { value: 4, label: 'Four Weeks' },
  { value: 5, label: 'Five Weeks' },
  { value: 6, label: 'Six Weeks' },
  { value: 7, label: 'Seven Weeks' },
  { value: 8, label: 'Eight Weeks' },
  { value: 9, label: 'More than 8 Weeks' },
]

const LATE_PAYMENTS = [
  { value: 0, label: 'No late payments' },
  { value: 1, label: '1 Late Payment' },
  { value: 2, label: '2 Late Payments' },
  { value: 3, label: '3 Late Payments' },
  { value: 4, label: '4 Late Payments' },
  { value: 5, label: '5 Late Payments' },
  { value: 6, label: '6 Late Payments' },
  { value: 7, label: '7 Late Payments' },
  { value: 8, label: '8 Late Payments' },
  { value: 9, label: 'More than 8 times' },
]

type DamageOption = {
  value: RentDeduction
  label: string
}

const DAMAGES: DamageOption[] = [
  { value: 'Uncertain', label: 'Unsure of damages' },
  { value: 'None', label: 'No Damages' },
  { value: 'Minor', label: 'Minor Damages' },
  { value: 'Major', label: 'Major Damages' },
]

const SubmitLandlordReferenceForm: React.FC<Props> = ({
  setOpen,
  reference,
  application,
}) => {
  const [error, setError] = useState(false)

  const [sending, setSending] = useState(false)

  const [startDate, setStartDate] = useState<Date>(getInitialStartDate())

  const [noticePeriod, setNoticePeriod] = useState<number>(
    reference?.term?.numberOfWeeksNotice ?? 1,
  )

  const [numberOfLatePayments, setNumberOfLatePayments] = useState<number>(
    reference.numberOfTimesRentWasLate ?? 0,
  )

  const [damages, setDamages] = useState<RentDeduction>(
    reference.rentDeductions ?? 'None',
  )

  function getInitialStartDate() {
    if (reference?.term?.startDate) return new Date(reference.term.startDate)
    const dateNow = new Date()
    dateNow.setFullYear(dateNow.getFullYear() - 1)
    return dateNow
  }

  const { personId } = useContext(ctx)

  function handleSubmit(e?: React.FormEvent) {
    if (e) e.preventDefault()
    const attachedSubjects = application.attachedSubjects ?? []

    const landlord = attachedSubjects.find(({ type }) => type === 'landlord')

    if (!landlord) {
      setError(true)
      return
    }

    setSending(true)
    setError(false)

    graphQLQuery({
      query: QUERIES.submitLandlordReference,
      variables: {
        request: {
          applicationId: application.applicationId,
          customerId: application.customerId,
          personId,
          landlordSubjectId: landlord?.subjectId,
          landlordReference: {
            address: reference.address,
            attachedSubjectId: reference.tenants[0].id,
            numberOfTimesLate: numberOfLatePayments,
            rentDeductions: damages,
            weeksNotice: noticePeriod,
            startDate,
          },
        },
      },
    })
      .then(resData => resData.json())
      .then(data => {
        if (data.errors) {
          setError(true)
        }
      })
      .catch(err => {
        console.log(err)
        setError(true)
        setSending(false)
      })
  }

  return (
    <SlideInSidebar
      onClose={() => setOpen(false)}
      header={
        <div>
          <h2>Complete Landlord Reference</h2>
        </div>
      }
      content={
        <Container onSubmit={handleSubmit}>
          <div>
            <p>
              This panel can be used to complete a landlord reference on behalf of the
              landlord.
            </p>
          </div>

          <div>
            <ItemHeader>Tenancy Start Date</ItemHeader>
            <DateInput
              date={startDate}
              setDate={date => setStartDate(date as Date)}
              endDate={new Date()}
            />
            <ItemHeader>Notice Period of Tenancy</ItemHeader>
            <InputGroup>
              <Select
                value={`${noticePeriod}`}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setNoticePeriod(+e.target.value)
                }
              >
                {WEEKS_NOTICE.map(({ label, value }, i) => (
                  <option key={i} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </InputGroup>
            <ItemHeader>Number of Late Rental Payments (More than 2 weeks)</ItemHeader>
            <InputGroup>
              <Select
                value={`${numberOfLatePayments}`}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setNumberOfLatePayments(+e.target.value)
                }
              >
                {LATE_PAYMENTS.map(({ label, value }, i) => (
                  <option key={i} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </InputGroup>

            <ItemHeader>Property Damage</ItemHeader>
            <InputGroup>
              <Select
                value={damages}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setDamages(e.target.value as RentDeduction)
                }
              >
                {DAMAGES.map(({ label, value }, i) => (
                  <option key={i} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </InputGroup>
          </div>
          {sending && (
            <div>
              <strong>
                <p>Submitting reference...</p>
              </strong>
              <p>Please keep this panel open while the reference submits</p>
            </div>
          )}
          {error && (
            <ErrorContainer>
              <p>
                <strong>Error:</strong> Failed to submit landlord reference. Please retry
                in a few minutes and contact support if issues persist.
              </p>
            </ErrorContainer>
          )}
        </Container>
      }
      footer={
        <div>
          <AlternateButton onClick={() => setOpen(false)}>Cancel</AlternateButton>
          <Button disabled={sending} onClick={() => handleSubmit()}>
            Confirm
          </Button>
        </div>
      }
    />
  )
}

export default SubmitLandlordReferenceForm
